import { Platform } from '@angular/cdk/platform';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ɵmarkDirty as markDirty,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { VirtualScrollerComponent } from 'ngx-virtual-scroller';
import { IPageInfo } from 'ngx-virtual-scroller/virtual-scroller';
import { Subject } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { trackByProp, trackByValue } from '@recruitee/common';
import { KeyboardEventCode } from '@recruitee/user-interface';

import {
  EmojiListNavigationKeys,
  EmojiRow,
  EmojiItem,
  EmojiVirtualScrollData,
  FlatArrayItemType,
} from '../types';

export const EMOJI_ROW_SIZE = 9;

@Component({
  selector: 'rt-emoji-list',
  templateUrl: './emoji-list.component.html',
  styleUrls: ['./emoji-list.component.less'],
  host: {
    '(keyup)': 'onKeyUp($event)',
  },
})
export class EmojiListComponent implements OnInit, OnDestroy {
  public readonly FlatArrayItemType = FlatArrayItemType;
  public filteredEmojis: EmojiVirtualScrollData;
  public virtualScrollData: EmojiVirtualScrollData;
  public searchEnabled = true;
  public isScrollAlwaysVisible = false;
  public searchFormControl = new FormControl('');
  public activeRow = 1;
  public activeCol = 0;
  public startIndexWithBuffer = 0;

  public queryChange: (value: string) => void;
  public select$ = new Subject<EmojiItem>();

  public trackByName = trackByProp<{ shortName: string }>({ prop: 'shortName' });
  public trackByValue = trackByValue;

  @ViewChild('virtualScrollerComponent', { static: true })
  private virtualScrollerComponent: VirtualScrollerComponent;

  constructor(private cdRef: ChangeDetectorRef, private platform: Platform) {}

  public ngOnInit(): void {
    this.searchFormControl.valueChanges
      .pipe(startWith(this.searchFormControl.value))
      .subscribe(value => this.queryChange(value));
  }

  public ngOnDestroy() {
    this.select$.complete();
  }

  public onMouseEnter(rowIndex: number, colIndex: number) {
    this.activeRow = rowIndex;
    this.activeCol = colIndex;
    this.cdRef.detectChanges();
  }

  public onKeyUp(event: KeyboardEvent) {
    this.handleKey(<EmojiListNavigationKeys>event.key);
  }

  public handleKey(code: EmojiListNavigationKeys) {
    if (KeyboardEventCode.ArrowUp === code) {
      let nextRowIndex = this.activeRow - 1;
      while (this.filteredEmojis[nextRowIndex]?.type === FlatArrayItemType.Heading) nextRowIndex--;

      this.activeRow = nextRowIndex < 0 ? this.activeRow : nextRowIndex;
      const row = <EmojiRow>this.filteredEmojis[this.activeRow];
      this.activeCol = this.activeCol < row.data.length ? this.activeCol : row.data.length - 1;

      const { startIndex, startIndexWithBuffer } = this.virtualScrollerComponent.viewPortInfo;
      if (this.activeRow - startIndexWithBuffer <= startIndex - startIndexWithBuffer) {
        this.virtualScrollerComponent.scrollToIndex(this.activeRow, true, -36, 0);
      }
    }

    if (KeyboardEventCode.ArrowDown === code) {
      let nextRowIndex = this.activeRow + 1;
      while (this.filteredEmojis[nextRowIndex]?.type === FlatArrayItemType.Heading) nextRowIndex++;

      this.activeRow = nextRowIndex < this.filteredEmojis.length ? nextRowIndex : this.activeRow;
      const row = <EmojiRow>this.filteredEmojis[this.activeRow];
      this.activeCol = this.activeCol < row.data.length ? this.activeCol : row.data.length - 1;

      const { endIndex, startIndexWithBuffer } = this.virtualScrollerComponent.viewPortInfo;
      if (this.activeRow - startIndexWithBuffer >= endIndex - startIndexWithBuffer) {
        this.virtualScrollerComponent.scrollToIndex(this.activeRow, false, 100, 0);
      }
    }

    if (KeyboardEventCode.ArrowRight === code) {
      this.activeCol = (this.activeCol + 1) % EMOJI_ROW_SIZE;
      const row = <EmojiRow>this.filteredEmojis[this.activeRow];
      this.activeCol = this.activeCol < row.data.length ? this.activeCol : 0;
    }

    if (KeyboardEventCode.ArrowLeft === code) {
      const row = <EmojiRow>this.filteredEmojis[this.activeRow];
      this.activeCol = (this.activeCol === 0 ? row.data.length : this.activeCol) - 1;
    }

    if (KeyboardEventCode.Enter === code) {
      const row = <EmojiRow>this.filteredEmojis[this.activeRow];
      this.select(row.data[this.activeCol]);
    }

    this.cdRef.detectChanges();
  }

  public listUpdate(data: EmojiVirtualScrollData) {
    this.virtualScrollData = data;
    this.isScrollAlwaysVisible = (this.platform.FIREFOX || this.platform.SAFARI) && data.length > 6;
    this.cdRef.detectChanges();
  }

  public onChange(event: IPageInfo) {
    this.startIndexWithBuffer = event.startIndexWithBuffer;
    markDirty(this);
  }

  public select(emoji: EmojiItem) {
    this.select$.next(emoji);
    this.select$.complete();
  }
}
